/* ==========================================================================
   Clínica Vie Et Santé — 02 Header con mega menú

   El panel abre con puntero y con teclado. Reglas que sostienen la pieza:
     - hidden se quita ANTES de animar y se repone DESPUÉS: mientras está
       cerrado, sus enlaces no son tabulables ni existen para un lector de
       pantalla.
     - Escape cierra y devuelve el foco al disparador. Sin eso, quien navega
       por teclado entra al panel y no tiene cómo salir.
     - El cierre por puntero tiene retardo: sin él, el trayecto del cursor
       desde el disparador hasta el panel lo cierra en el camino.
     - Un solo panel abierto a la vez.
   ========================================================================== */

class VesMegaMenu {
  constructor(root) {
    this.root = root;
    this.triggers = Array.from(root.querySelectorAll('[data-trigger]'));
    if (this.triggers.length === 0) return;

    this.open = null;
    this.closeTimer = null;
    // M-11: como se abrio el panel activo, 'hover' o 'click'. Sin este dato el
    // clic no puede distinguir "cerrar lo que abri" de "cerrar lo que el mouse
    // abrio de paso", que era la causa de los dos clics.
    this.openedBy = null;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.triggers.forEach((t) => this.bindTrigger(t));
    this.bindGlobal();
  }

  panelOf(trigger) {
    return document.getElementById(trigger.getAttribute('aria-controls'));
  }

  show(trigger) {
    clearTimeout(this.closeTimer);
    if (this.open === trigger) return;
    if (this.open) this.hide(this.open, { instant: true });

    const panel = this.panelOf(trigger);
    if (!panel) return;

    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    this.open = trigger;

    // Un frame de margen: si se quita hidden y se agrega la clase en el mismo
    // tick, el navegador no tiene estado inicial desde el cual animar.
    if (this.reduced) {
      panel.classList.add('is-open');
    } else {
      requestAnimationFrame(() => panel.classList.add('is-open'));
    }
  }

  hide(trigger, opts) {
    const panel = this.panelOf(trigger);
    if (!panel) return;

    trigger.setAttribute('aria-expanded', 'false');
    panel.classList.remove('is-open');
    if (this.open === trigger) {
      this.open = null;
      this.openedBy = null;
    }

    const finish = () => {
      if (!panel.classList.contains('is-open')) panel.hidden = true;
    };

    if ((opts && opts.instant) || this.reduced) finish();
    else setTimeout(finish, 320);
  }

  hideAll() {
    this.triggers.forEach((t) => {
      if (t.getAttribute('aria-expanded') === 'true') this.hide(t);
    });
  }

  scheduleClose(trigger) {
    clearTimeout(this.closeTimer);
    // 180ms: alcanza para cruzar el hueco entre el disparador y el panel, y no
    // tanto como para que el menú se sienta pegajoso.
    this.closeTimer = setTimeout(() => this.hide(trigger), 180);
  }

  bindTrigger(trigger) {
    const panel = this.panelOf(trigger);
    const item = trigger.closest('.ves-header__bar-item');

    trigger.addEventListener('click', () => {
      // M-11: con mouse, pointerenter ya dejo el panel abierto, asi que el
      // trigger llegaba aca con aria-expanded=true y el primer clic lo cerraba:
      // hacian falta dos clics para pasar de un panel a otro. Ahora solo se
      // alterna si fue el propio clic el que lo abrio; si venia de hover, el
      // clic lo confirma.
      if (trigger.getAttribute('aria-expanded') === 'true' && this.openedBy === 'click') {
        this.hide(trigger);
      } else {
        this.show(trigger);
        this.openedBy = 'click';
      }
    });

    if (item) {
      item.addEventListener('pointerenter', (e) => {
        // Solo con mouse: en táctil el hover no existe y abrir aquí haría que
        // el primer toque abra y cierre a la vez.
        if (e.pointerType === 'mouse') {
          this.show(trigger);
          this.openedBy = 'hover';
        }
      });
      item.addEventListener('pointerleave', (e) => {
        if (e.pointerType === 'mouse') this.scheduleClose(trigger);
      });
    }

    // El foco entrando al panel cancela cualquier cierre pendiente.
    if (panel) {
      panel.addEventListener('focusin', () => clearTimeout(this.closeTimer));
    }

    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' && panel) {
        e.preventDefault();
        this.show(trigger);
        const first = panel.querySelector('a, button');
        if (first) first.focus();
      }
    });
  }

  bindGlobal() {
    this.root.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !this.open) return;
      const trigger = this.open;
      this.hide(trigger);
      trigger.focus();
    });

    // El foco salió del header entero: no queda nadie mirando el panel.
    this.root.addEventListener('focusout', (e) => {
      if (!this.root.contains(e.relatedTarget)) this.hideAll();
    });

    document.addEventListener('click', (e) => {
      if (!this.root.contains(e.target)) this.hideAll();
    });
  }
}

function initVesHeader() {
  document.querySelectorAll('[data-ves-header]').forEach((el) => {
    if (el.dataset.vesInit === 'true') return;
    el.dataset.vesInit = 'true';
    new VesMegaMenu(el);
  });
}

document.addEventListener('DOMContentLoaded', initVesHeader);
document.addEventListener('shopify:section:load', initVesHeader);
